<script setup>
import { computed, ref, watch } from 'vue'
import { useDocumentVisibility } from '@vueuse/core'

/**
 * Constant representing the maximum number of laps.
 *
 * @type {Number}
 */
const MAX_LAPS = 6

/**
 * Constant representing the maximum time.
 *
 * @type {Number}
 */
const MAX_TIME = 300

/**
 * Define the component's emits.
 *
 * @type {Array}
 */
const emits = defineEmits(['refresh'])

/**
 * Define the component's data properties.
 */
let interval = null
const laps = ref(0)
const timer = ref(0)
const show = ref(false)
const isPageVisible = ref(useDocumentVisibility())

/**
 * Determine if the snackbar is visible.
 *
 * @return {Boolean}
 */
const isVisible = computed(() => {
    return showManualRefresh.value || showCountdown.value
})

/**
 * Determine if the manual refresh button should be shown.
 *
 * @return {Boolean}
 */
const showManualRefresh = computed(() => laps.value === MAX_LAPS)

/**
 * Determine if the countdown should be shown.
 *
 * @return {Boolean}
 */
const showCountdown = computed(() => {
    return !showManualRefresh.value && timeRemaining.value <= 15
})

/**
 * Determine the remaining time.
 *
 * @return {Number}
 */
const timeRemaining = computed(() => {
    return MAX_TIME - timer.value
})

/**
 * Determine if the end of the lap has been reached.
 *
 * @return {Boolean}
 */
const isEndOfLap = () => {
    return timer.value === MAX_TIME && laps.value < MAX_LAPS
}

/**
 * Determine if the last lap has been reached.
 *
 * @return {Boolean}
 */
const isLastLap = () => {
    return laps.value === MAX_LAPS
}

/**
 * Handle the cancel event.
 *
 * @return {void}
 */
const onCancel = () => {
    timer.value = 0
}

/**
 * Handle the close event.
 *
 * @return {void}
 */
const onClose = () => {
    show.value = false
}

/**
 * Handle the refresh event.
 *
 * @return {void}
 */
const onRefresh = () => {
    laps.value = 0
    timer.value = 0

    emits('refresh')
    startTimer()
}

/**
 * Determine if the timer should be incremented.
 *
 * @return {Boolean}
 */
const shouldIncrementTimer = () => {
    return timer.value < MAX_TIME && laps.value < MAX_LAPS
}

/**
 * Start the timer.
 *
 * @return {void}
 */
const startTimer = () => {
    interval = setInterval(() => {
        if (shouldIncrementTimer()) {
            timer.value++
        }

        if (isEndOfLap()) {
            laps.value++
            timer.value = 0

            emits('refresh')
        }

        if (isLastLap()) {
            clearInterval(interval)
        }
    }, 1000)
}

/**
 * Watch the page visibility.
 *
 * @param {Boolean} value
 * @return {void}
 */
watch(
    isPageVisible,
    (value) => {
        if (value === 'visible') {
            return startTimer()
        }

        interval = clearInterval(interval)
    },
    { immediate: true }
)

/**
 * Watch the snackbar visibility.
 *
 * @param {Boolean} value
 * @return {void}
 */
watch(isVisible, (value) => {
    if (value) {
        return (show.value = true)
    }

    show.value = false
})
</script>

<template>
    <v-snackbar rounded v-model="show" :timeout="-1">
        <template v-if="showCountdown" #text>
            Refreshing in {{ timeRemaining }} seconds
        </template>
        <template v-else-if="showManualRefresh" #text>
            Auto refresh is disabled.
        </template>

        <template v-slot:actions>
            <v-btn
                v-if="showCountdown"
                color="white"
                variant="text"
                @click="onCancel"
            >
                Cancel
            </v-btn>
            <v-btn
                v-else-if="showManualRefresh"
                color="white"
                variant="text"
                class="mr-1"
                @click="onRefresh"
            >
                Refresh
            </v-btn>
            <v-btn icon size="x-small" @click="onClose">
                <v-icon size="x-small" icon="fal fa-xmark"> </v-icon>
            </v-btn>
        </template>
    </v-snackbar>
</template>
